/**
 * Journal Page — Főkönyvi napló (General Ledger / Journal), 19-kör FE-1.
 *
 * A 19-kör BE-1 (#597 `postFromInvoice`) + #596 (kaució) double-entry tételeit jeleníti meg:
 * journal entries + a hozzájuk tartozó könyvelési sorok (kettős könyvvitel). Read-only nézet —
 * az összegeket a BE adja (Decimal→string), itt csak megjelenítjük (`Number()` → HUF-formázás),
 * FE-aritmetika nélkül. A balansz (totalDebit==totalCredit) string-egyenlőség.
 *
 * API: GET /api/v1/accounting/journal?from=YYYY-MM-DD&to=YYYY-MM-DD[&sourceType=]
 */
import { getJournalEntries, type JournalEntry, type JournalSourceType } from '@/api/accounting';
import { Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BookOpen, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

// ============================================
// Types & helpers
// ============================================

type PeriodRange = 'month' | 'quarter' | 'year';
type SourceFilter = JournalSourceType | 'ALL';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** A preset-időszakot from/to (YYYY-MM-DD, inkluzív) dátum-párrá alakítja. */
function getPeriodRange(range: PeriodRange): { from: string; to: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed

  if (range === 'year') {
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  }
  if (range === 'quarter') {
    const startMonth = Math.floor(month / 3) * 3;
    const endMonth = startMonth + 2;
    const lastDay = new Date(year, endMonth + 1, 0).getDate();
    return {
      from: `${year}-${pad(startMonth + 1)}-01`,
      to: `${year}-${pad(endMonth + 1)}-${pad(lastDay)}`,
    };
  }
  const lastDay = new Date(year, month + 1, 0).getDate();
  return { from: `${year}-${pad(month + 1)}-01`, to: `${year}-${pad(month + 1)}-${pad(lastDay)}` };
}

/** Forrás-típusonkénti badge-osztály (a sourceType-oszlophoz). */
const SOURCE_BADGE_CLASS: Record<JournalSourceType, string> = {
  INVOICE: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  SALE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  RENTAL_DEPOSIT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  RENTAL_DEPOSIT_REFUND: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  RENTAL_DEPOSIT_FORFEIT: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const SOURCE_LABEL_KEY: Record<JournalSourceType, string> = {
  INVOICE: 'finance.journalSourceInvoice',
  SALE: 'finance.journalSourceSale',
  RENTAL_DEPOSIT: 'finance.journalSourceRentalDeposit',
  RENTAL_DEPOSIT_REFUND: 'finance.journalSourceRentalDepositRefund',
  RENTAL_DEPOSIT_FORFEIT: 'finance.journalSourceRentalDepositForfeit',
};

const SOURCE_FILTERS: SourceFilter[] = [
  'ALL',
  'INVOICE',
  'SALE',
  'RENTAL_DEPOSIT',
  'RENTAL_DEPOSIT_REFUND',
  'RENTAL_DEPOSIT_FORFEIT',
];

// ============================================
// Hook
// ============================================

function useJournalEntries(range: PeriodRange, sourceType: SourceFilter) {
  const { from, to } = useMemo(() => getPeriodRange(range), [range]);

  const query = useQuery({
    queryKey: ['journal', from, to, sourceType],
    queryFn: () =>
      getJournalEntries({
        from,
        to,
        ...(sourceType !== 'ALL' ? { sourceType } : {}),
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    entries: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

// ============================================
// Component
// ============================================

export function JournalPage() {
  const { t } = useTranslation();
  const [range, setRange] = useState<PeriodRange>('month');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('ALL');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { entries, isLoading, isError, error } = useJournalEntries(range, sourceFilter);

  const formatCurrency = (amount: string) =>
    new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(Number(amount));

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Balansz: a kettős könyvvitel akkor helyes, ha a tartozik == követel (string-egyenlőség). */
  const isBalanced = (e: JournalEntry) => e.totalDebit === e.totalCredit;

  const periodOptions: PeriodRange[] = ['month', 'quarter', 'year'];
  const periodLabelKey: Record<PeriodRange, string> = {
    month: 'finance.periodMonth',
    quarter: 'finance.periodQuarter',
    year: 'finance.periodYear',
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3">
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <p className="text-destructive">{t('report.errorLoading')}</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : String(error ?? '')}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg p-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <BookOpen className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('finance.journalTitle')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
            {t('finance.journalSubtitle')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Period range */}
        <div
          className="flex gap-1 rounded-lg bg-gray-100 dark:bg-[#1f5f6b] p-1"
          role="radiogroup"
          aria-label={t('finance.periodSelector')}
        >
          {periodOptions.map(option => (
            <button
              key={option}
              role="radio"
              aria-checked={range === option}
              onClick={() => setRange(option)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                range === option
                  ? 'bg-white dark:bg-[#1a4f5c] text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-[#ccfbf1] hover:text-gray-900 dark:hover:text-white'
              )}
            >
              {t(periodLabelKey[option])}
            </button>
          ))}
        </div>

        {/* Source type filter */}
        <div
          className="flex flex-wrap gap-1"
          role="radiogroup"
          aria-label={t('finance.journalSourceFilter')}
        >
          {SOURCE_FILTERS.map(sf => (
            <button
              key={sf}
              role="radio"
              aria-checked={sourceFilter === sf}
              onClick={() => setSourceFilter(sf)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                sourceFilter === sf
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#1f5f6b] dark:text-[#ccfbf1]'
              )}
            >
              {sf === 'ALL' ? t('finance.journalSourceAll') : t(SOURCE_LABEL_KEY[sf])}
            </button>
          ))}
        </div>

        <span className="ml-auto text-sm text-muted-foreground">
          {t('finance.journalEntryCount', { count: entries.length })}
        </span>
      </div>

      {/* Entries table */}
      <Card>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <BookOpen className="h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-muted-foreground">{t('finance.journalEmpty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="w-8 px-2 py-3" />
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#ccfbf1]">
                      {t('finance.journalColEntryNumber')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#ccfbf1]">
                      {t('finance.journalColDate')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#ccfbf1]">
                      {t('finance.journalColDescription')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#ccfbf1]">
                      {t('finance.journalColSource')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#ccfbf1]">
                      {t('finance.journalColDebit')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-[#ccfbf1]">
                      {t('finance.journalColCredit')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {entries.map(entry => {
                    const expanded = expandedIds.has(entry.id);
                    const balanced = isBalanced(entry);
                    return (
                      <FragmentRow
                        key={entry.id}
                        entry={entry}
                        expanded={expanded}
                        balanced={balanced}
                        onToggle={() => toggleExpand(entry.id)}
                        formatCurrency={formatCurrency}
                        sourceBadgeLabel={t(SOURCE_LABEL_KEY[entry.sourceType])}
                        unbalancedLabel={t('finance.journalUnbalanced')}
                        accountColLabel={t('finance.journalColAccount')}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// Row (entry + expandable lines)
// ============================================

interface FragmentRowProps {
  entry: JournalEntry;
  expanded: boolean;
  balanced: boolean;
  onToggle: () => void;
  formatCurrency: (amount: string) => string;
  sourceBadgeLabel: string;
  unbalancedLabel: string;
  accountColLabel: string;
}

function FragmentRow({
  entry,
  expanded,
  balanced,
  onToggle,
  formatCurrency,
  sourceBadgeLabel,
  unbalancedLabel,
  accountColLabel,
}: FragmentRowProps) {
  return (
    <>
      <tr
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f3d47]"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <td className="px-2 py-3 text-gray-400">
          {expanded ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
          {entry.entryNumber}
          {!balanced && (
            <span className="ml-2 inline-flex items-center" title={unbalancedLabel}>
              <AlertTriangle
                className="h-3.5 w-3.5 text-destructive"
                aria-label={unbalancedLabel}
              />
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
          {entry.entryDate}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{entry.description}</td>
        <td className="whitespace-nowrap px-4 py-3 text-sm">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              SOURCE_BADGE_CLASS[entry.sourceType]
            )}
          >
            {sourceBadgeLabel}
          </span>
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-white">
          {formatCurrency(entry.totalDebit)}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-white">
          {formatCurrency(entry.totalCredit)}
        </td>
      </tr>
      {expanded &&
        entry.lines.map((line, idx) => (
          <tr key={`${entry.id}-${idx}`} className="bg-gray-50/60 dark:bg-[#0c333c]">
            <td />
            <td className="px-4 py-2 text-xs text-gray-500 dark:text-[#9fdccb]" colSpan={3}>
              <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">
                {line.accountCode}
              </span>{' '}
              <span className="text-gray-500 dark:text-[#ccfbf1]">{line.accountName}</span>
              <span className="sr-only">{accountColLabel}</span>
            </td>
            <td />
            <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-gray-600 dark:text-gray-300">
              {Number(line.debitAmount) > 0 ? formatCurrency(line.debitAmount) : ''}
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-gray-600 dark:text-gray-300">
              {Number(line.creditAmount) > 0 ? formatCurrency(line.creditAmount) : ''}
            </td>
          </tr>
        ))}
    </>
  );
}
